import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BookOpen } from "lucide-react";
import { expireTeacherSession } from "../../features/teacher/teacherSlice";

const IDLE_TIMEOUT_MS = 10 * 60 * 1000;
const WARNING_TIMEOUT_MS = 60 * 1000;
const WARNING_DELAY_MS = IDLE_TIMEOUT_MS - WARNING_TIMEOUT_MS;
const COUNTDOWN_INTERVAL_MS = 250;
const ACTIVITY_THROTTLE_MS = 1000;
const DEFAULT_SECONDS_REMAINING = Math.ceil(WARNING_TIMEOUT_MS / 1000);
const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

const warningStyles = `
  .teacher-session-warning-overlay {
    position: fixed;
    inset: 0;
    z-index: 135;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.25rem;
    background: rgba(24, 19, 15, 0.48);
    backdrop-filter: blur(7px);
  }

  .teacher-session-warning-card {
    width: min(100%, 29rem);
    border: 1px solid #eadfce;
    border-radius: 1.5rem;
    background:
      radial-gradient(circle at top left, rgba(255, 194, 142, 0.22), transparent 30%),
      linear-gradient(180deg, #fffdf8 0%, #fbf5eb 100%);
    box-shadow: 0 30px 50px rgba(44, 33, 18, 0.18);
    padding: 1.5rem;
    color: #1f1a17;
    font-family: "Lato", sans-serif;
  }

  .teacher-session-warning-badge {
    width: 3rem;
    height: 3rem;
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: linear-gradient(135deg, #ff7154 0%, #ff564d 100%);
    color: #ffffff;
    box-shadow: 0 12px 20px rgba(255, 98, 77, 0.22);
    margin-bottom: 1rem;
  }

  .teacher-session-warning-title {
    margin: 0 0 0.55rem;
    font-size: 1.3rem;
    font-weight: 800;
  }

  .teacher-session-warning-copy {
    margin: 0;
    color: #67615c;
    line-height: 1.6;
  }

  .teacher-session-warning-action {
    margin-top: 1.25rem;
    width: 100%;
    min-height: 3.2rem;
    border: 0;
    border-radius: 0.95rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #ff7154 0%, #ff564d 100%);
    box-shadow: 0 18px 28px rgba(255, 98, 77, 0.22);
    color: #ffffff;
    font: inherit;
    font-size: 0.98rem;
    font-weight: 800;
    cursor: pointer;
  }
`;

function TeacherSessionWarningModal({ secondsRemaining, onStayLoggedIn }) {
  return (
    <>
      <style>{warningStyles}</style>
      <div className="teacher-session-warning-overlay" role="dialog" aria-modal="true">
        <div className="teacher-session-warning-card">
          <div className="teacher-session-warning-badge" aria-hidden="true">
            <BookOpen size={20} />
          </div>
          <h2 className="teacher-session-warning-title">Session timeout warning</h2>
          <p className="teacher-session-warning-copy">
            You will be logged out in {secondsRemaining} second
            {secondsRemaining === 1 ? "" : "s"} due to inactivity.
          </p>
          <p className="teacher-session-warning-copy" style={{ marginTop: "0.35rem" }}>
            Select stay logged in to continue using the teacher portal.
          </p>
          <button
            type="button"
            className="teacher-session-warning-action"
            onClick={onStayLoggedIn}
          >
            Stay Logged In
          </button>
        </div>
      </div>
    </>
  );
}

export default function TeacherSessionTimeoutManager() {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.teacher?.token);
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(
    DEFAULT_SECONDS_REMAINING
  );

  const warningTimeoutRef = useRef(null);
  const logoutTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const warningDeadlineRef = useRef(0);
  const lastHandledActivityAtRef = useRef(0);

  const clearTimer = (timerRef) => {
    if (!timerRef.current) return;
    window.clearTimeout(timerRef.current);
    window.clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const clearAllTimers = useCallback(() => {
    clearTimer(warningTimeoutRef);
    clearTimer(logoutTimeoutRef);
    clearTimer(countdownIntervalRef);
  }, []);

  const updateCountdown = useCallback(() => {
    const remainingMs = Math.max(warningDeadlineRef.current - Date.now(), 0);
    setSecondsRemaining(Math.ceil(remainingMs / 1000));
  }, []);

  const handleSessionExpired = useCallback(() => {
    clearAllTimers();
    setIsWarningVisible(false);
    setSecondsRemaining(DEFAULT_SECONDS_REMAINING);
    dispatch(expireTeacherSession());
  }, [clearAllTimers, dispatch]);

  const showWarning = useCallback(() => {
    warningDeadlineRef.current = Date.now() + WARNING_TIMEOUT_MS;
    setIsWarningVisible(true);
    updateCountdown();
    clearTimer(countdownIntervalRef);
    countdownIntervalRef.current = window.setInterval(
      updateCountdown,
      COUNTDOWN_INTERVAL_MS
    );
  }, [updateCountdown]);

  const scheduleInactivityTimers = useCallback(
    (startedAt = Date.now()) => {
      clearAllTimers();
      warningDeadlineRef.current = startedAt + IDLE_TIMEOUT_MS;
      warningTimeoutRef.current = window.setTimeout(showWarning, WARNING_DELAY_MS);
      logoutTimeoutRef.current = window.setTimeout(
        handleSessionExpired,
        IDLE_TIMEOUT_MS
      );
    },
    [clearAllTimers, handleSessionExpired, showWarning]
  );

  const registerActivity = useCallback(
    (force = false) => {
      const now = Date.now();
      if (!force && now - lastHandledActivityAtRef.current < ACTIVITY_THROTTLE_MS) {
        return;
      }

      lastHandledActivityAtRef.current = now;
      setIsWarningVisible(false);
      setSecondsRemaining(DEFAULT_SECONDS_REMAINING);
      scheduleInactivityTimers(now);
    },
    [scheduleInactivityTimers]
  );

  useEffect(() => {
    if (!token) {
      clearAllTimers();
      setIsWarningVisible(false);
      setSecondsRemaining(DEFAULT_SECONDS_REMAINING);
      return undefined;
    }

    registerActivity(true);
    const handleActivity = () => registerActivity(false);

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
      clearAllTimers();
    };
  }, [clearAllTimers, registerActivity, token]);

  if (!token || !isWarningVisible) return null;

  return (
    <TeacherSessionWarningModal
      secondsRemaining={secondsRemaining}
      onStayLoggedIn={() => registerActivity(true)}
    />
  );
}
